import { create } from 'zustand';
import { ReportBundleParams, ReportBundleSummary, ReportBundleVersionParams, ReportBundleVersionSummary } from '@/backend/access/reportBundle/types';
import { ReportBundleDiagnostic, ReportBundleFiles } from '@/reports/bundles/types';
import { BundleSchemaInfo, CreateReportBundleRequest, UpdateReportBundleRequest } from '@/types/reportBundleTypes';
import { AssistantMessage, AssistantStreamEvent } from '@/types/reportAssistantTypes';
import { RunReportContext } from '@/reports/types';
import { I18n } from '@/i18n/I18n';
import { ReportBundleFrontendController } from '../controllers/reportBundleFrontendController';

/** A bundle as the studio edits it: `bundle.draftFiles` are its working files, the schema's draft among them as `inputs.json` when it follows it. */
export interface ReportBundleEntry {
  bundle: ReportBundleParams;
  versions: ReportBundleVersionSummary[];
  diagnostics: ReportBundleDiagnostic[];
  /** the schema it reads; null for a run report */
  schema: BundleSchemaInfo | null;
  /** the hash of `bundle.draftFiles` as the server holds them: a publish names it, so only the draft shown and checked goes out */
  filesHash: string | null;
}

export interface ReportSdkTypesState {
  files: Record<string, string>;
  paths: Record<string, string[]>;
}

interface ReportBundleStore {
  bundles: ReportBundleSummary[] | null;
  bundlesById: Record<string, ReportBundleEntry>;
  versionsById: Record<string, ReportBundleVersionParams>;
  sdkTypes: ReportSdkTypesState | null;
  /** whether the server can run the studio's assistant, and with which model; null until asked */
  assistantStatus: { enabled: boolean; model: string | null } | null;

  fetchBundles: () => Promise<void>;
  fetchBundle: (id: string) => Promise<ReportBundleEntry | null>;
  createBundle: (data: CreateReportBundleRequest) => Promise<ReportBundleParams | null>;
  updateMeta: (id: string, data: UpdateReportBundleRequest) => Promise<boolean>;
  /**
   * compile and store the draft; the diagnostics come back whether or not it compiled. `quiet`: no toast.
   * `schemaDraftHash`: the schema draft the files' inputs.json was opened from; `schemaConflict` comes back
   * true when it was not saved, because that draft changed since
   */
  /** `expectedDraftHash`: the entry's `filesHash` the files were taken from; refused when the draft was saved elsewhere since (the entry is read again) */
  saveDraft: (id: string, files: ReportBundleFiles, options?: { quiet?: boolean; schemaDraftHash?: string; expectedDraftHash?: string }) => Promise<(ReportBundleEntry & { schemaConflict: boolean }) | null>;
  /**
   * `publishSchema`: publish the schema's draft as its next version too, when the bundle follows a draft not yet published.
   * `schemaDraftHash`: the schema draft the studio last saw; the server refuses to publish a draft that changed since
   */
  /** `expectedDraftHash`: the entry's `filesHash` the author was shown; refused when the draft was saved elsewhere since (the entry is read again) */
  publish: (id: string, note?: string, options?: { publishSchema?: boolean; schemaDraftHash?: string; expectedDraftHash?: string }) => Promise<ReportBundleVersionParams | null>;
  fetchVersion: (id: string, versionId: string) => Promise<ReportBundleVersionParams | null>;
  deleteBundle: (id: string) => Promise<boolean>;
  /** drop what is cached of these bundles, read again when next opened: their schema's draft, among their files, changed elsewhere */
  forgetBundles: (ids: string[]) => void;
  fetchSdkTypes: () => Promise<ReportSdkTypesState | null>;
  /** a run as the bundle's saved draft reads it, or why it could not be read */
  readPreviewContext: (id: string, runId: string) => Promise<{ context: RunReportContext } | { error: string }>;
  fetchAssistantStatus: () => Promise<void>;
  /** stream one reply of the assistant; throws with the server's message when it refuses */
  streamAssistantReply: (id: string, messages: AssistantMessage[], onEvent: (event: AssistantStreamEvent) => void, signal: AbortSignal) => Promise<void>;
  reset: () => void;
}

const initialState = {
  bundles: null as ReportBundleSummary[] | null,
  bundlesById: {} as Record<string, ReportBundleEntry>,
  versionsById: {} as Record<string, ReportBundleVersionParams>,
  sdkTypes: null as ReportSdkTypesState | null,
  assistantStatus: null as { enabled: boolean; model: string | null } | null,
};

export const useReportBundleStore = create<ReportBundleStore>((set, get) => ({
  ...initialState,

  fetchBundles: async () => {
    const response = await new ReportBundleFrontendController().list();
    if (response.status !== 200) return;
    set({ bundles: response.bundles });
  },

  fetchBundle: async (id) => {
    const response = await new ReportBundleFrontendController().read(id);
    if (response.status !== 200 || !response.bundle) return null;
    const entry: ReportBundleEntry = { bundle: response.bundle, versions: response.versions ?? [], diagnostics: response.diagnostics ?? [], schema: response.schema ?? null, filesHash: response.filesHash ?? null };
    set({ bundlesById: { ...get().bundlesById, [id]: entry } });
    return entry;
  },

  createBundle: async (data) => {
    const response = await new ReportBundleFrontendController().create(data);
    if (response.status !== 201 || !response.bundle) return null;
    set({
      bundlesById: { ...get().bundlesById, [response.bundle.id]: { bundle: response.bundle, versions: [], diagnostics: response.diagnostics ?? [], schema: response.schema ?? null, filesHash: response.filesHash ?? null } },
      bundles: null,
    });
    return response.bundle;
  },

  updateMeta: async (id, data) => {
    const response = await new ReportBundleFrontendController().update(id, data);
    if (response.status !== 200 || !response.bundle) return false;
    const current = get().bundlesById[id];
    set({ bundlesById: { ...get().bundlesById, [id]: { ...current, bundle: response.bundle, schema: response.schema ?? current?.schema ?? null, filesHash: response.filesHash ?? current?.filesHash ?? null } }, bundles: null });
    return true;
  },

  saveDraft: async (id, files, options) => {
    const response = await new ReportBundleFrontendController().saveDraft(id, {
      files,
      ...(options?.quiet ? { quiet: true } : {}),
      ...(options?.schemaDraftHash ? { schemaDraftHash: options.schemaDraftHash } : {}),
      ...(options?.expectedDraftHash ? { expectedDraftHash: options.expectedDraftHash } : {}),
    });
    // refused for a draft saved elsewhere since: read again, for the studio to show the draft as it is now
    if (response.status === 422 && options?.expectedDraftHash) await get().fetchBundle(id);
    if (response.status !== 200 || !response.bundle) return null;
    const current = get().bundlesById[id];
    const entry: ReportBundleEntry = { bundle: response.bundle, versions: current?.versions ?? [], diagnostics: response.diagnostics ?? [], schema: response.schema ?? null, filesHash: response.filesHash ?? null };
    set({ bundlesById: { ...get().bundlesById, [id]: entry }, bundles: null });
    return { ...entry, schemaConflict: !!response.schemaConflict };
  },

  publish: async (id, note, options) => {
    const response = await new ReportBundleFrontendController().publish(id, {
      note,
      ...(options?.publishSchema ? { publishSchema: true } : {}),
      ...(options?.schemaDraftHash ? { schemaDraftHash: options.schemaDraftHash } : {}),
      ...(options?.expectedDraftHash ? { expectedDraftHash: options.expectedDraftHash } : {}),
    });
    // refused, perhaps for a draft saved elsewhere since: read again, for the studio to show the draft as it is now
    if (response.status === 422 && (options?.schemaDraftHash || options?.expectedDraftHash)) await get().fetchBundle(id);
    if (response.status !== 201 || !response.version) return null;
    const version = response.version;
    set({ versionsById: { ...get().versionsById, [version.id]: version }, bundles: null });
    // The entry is re-read rather than patched: the version list gained one, and the title follows the published definition.
    await get().fetchBundle(id);
    return version;
  },

  fetchVersion: async (id, versionId) => {
    const cached = get().versionsById[versionId];
    if (cached) return cached;
    const response = await new ReportBundleFrontendController().readVersion(id, versionId);
    if (response.status !== 200 || !response.version) return null;
    set({ versionsById: { ...get().versionsById, [versionId]: response.version } });
    return response.version;
  },

  deleteBundle: async (id) => {
    const response = await new ReportBundleFrontendController().delete(id);
    if (response.status !== 200) return false;
    const { [id]: _gone, ...rest } = get().bundlesById;
    set({ bundlesById: rest, bundles: get().bundles?.filter((b) => b.id !== id) ?? null });
    return true;
  },

  forgetBundles: (ids) => {
    const bundlesById = { ...get().bundlesById };
    for (const id of ids) delete bundlesById[id];
    set({ bundlesById });
  },

  fetchSdkTypes: async () => {
    const cached = get().sdkTypes;
    if (cached) return cached;
    const response = await new ReportBundleFrontendController().sdkTypes();
    if (response.status !== 200) return null;
    const sdkTypes = { files: response.files, paths: response.paths };
    set({ sdkTypes });
    return sdkTypes;
  },

  readPreviewContext: async (id, runId) => {
    const response = await new ReportBundleFrontendController().previewContext(id, runId);
    if (response.status === 200 && response.context) return { context: response.context };
    return { error: response.errors?.[0]?.message ?? I18n.get('reportBundleRunNotLoaded') };
  },

  fetchAssistantStatus: async () => {
    if (get().assistantStatus) return;
    const response = await new ReportBundleFrontendController().assistantStatus();
    set({ assistantStatus: response.status === 200 ? { enabled: !!response.enabled, model: response.model ?? null } : { enabled: false, model: null } });
  },

  streamAssistantReply: async (id, messages, onEvent, signal) => {
    const refused = await new ReportBundleFrontendController().assistantReply(id, messages, onEvent, signal);
    if (refused) throw new Error(refused.errors?.[0]?.message || I18n.get('reportAssistantRequestFailed', { message: `HTTP ${refused.status}` }));
  },

  reset: () => set({ ...initialState }),
}));
